import { useCallback, useEffect, useRef, useState } from "react";

import { CursorPage, ViewState } from "../types/alerting";

export type PageFetcher<T> = (cursor: string | null) => Promise<CursorPage<T>>;

export interface CursorPagination<T> {
  rows: T[];
  state: ViewState;
  hasMore: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMore: () => void;
  reload: () => void;
}

const MAX_EMPTY_BATCH_CHASES = 5;

/** Prefers the server's own explanation over axios' generic one. */
const messageOf = (error: any): string =>
  error?.response?.data?.message ?? error?.message ?? "Request failed";

const isRefusal = (error: any): boolean => error?.response?.status === 403;

/**
 * Accumulates cursor-paged batches and reports exactly one of the display states.
 * The fetcher must be referentially stable: a new one restarts from the first batch.
 */
export function useCursorPagination<T>(fetchPage: PageFetcher<T>): CursorPagination<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [state, setState] = useState<ViewState>("loading");
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const rowsRef = useRef<T[]>([]);
  const generation = useRef(0);

  const load = useCallback(async (from: string | null) => {
    const mine = ++generation.current;
    const initial = from === null;

    if (initial) {
      rowsRef.current = [];
      setRows([]);
      setCursor(null);
      setState("loading");
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const batch: T[] = [];
      let next: string | null = from;
      let chases = 0;

      do {
        const page = await fetchRef.current(next);
        if (mine !== generation.current) return;
        batch.push(...(page?.content ?? []));
        next = page?.nextCursor ?? null;
        chases += 1;
      } while (batch.length === 0 && next !== null && chases < MAX_EMPTY_BATCH_CHASES);

      const merged = initial ? batch : [...rowsRef.current, ...batch];
      rowsRef.current = merged;
      setRows(merged);
      setCursor(next);
      setState(merged.length > 0 ? "ready" : "empty");
    } catch (e) {
      if (mine !== generation.current) return;
      setError(messageOf(e));
      if (initial) setState(isRefusal(e) ? "refused" : "failed");
    } finally {
      if (mine === generation.current) setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void load(null);
  }, [fetchPage, load]);

  const loadMore = useCallback(() => {
    if (cursor === null || loadingMore || state === "loading") return;
    void load(cursor);
  }, [cursor, loadingMore, state, load]);

  const reload = useCallback(() => {
    void load(null);
  }, [load]);

  return {
    rows,
    state,
    hasMore: cursor !== null,
    loadingMore,
    error,
    loadMore,
    reload,
  };
}

export default useCursorPagination;
